import { toast } from 'react-toastify';
import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import FullPageLoader from '@src/common/FullPageLoader';
import { NavLink, useSearchParams, useNavigate, useParams } from 'react-router-dom';
import secureStorage from '@src/config/encrypt';
import { DocumentForm } from './DocumentForm';
import { getFormConfig, getLeadDetails, saveInsurance, updateDocumentStatus, updateFormConfig, updateInsuranceDataForAgency, uploadAndReadDocument, generateDocUrls, setEditDocuments, fetchAllDocuments, setResubmissionLeadData } from '@src/store/slices/insurance';
import { addDocument, getDocumentListing, getMasterConfig, addDocumentWeb } from '@src/store/slices/common';
import VehicleDetails from './VehicleDetails';
import Plans from './Plans';
import { APP_TYPE_AGENCY, DOCUMENT_CATEGORY, INS_TYPE_CTPL, WITH_RULE_ENGINE, WITHOUT_RULE_ENGINE,EFFECTIVITY_EXPIRY_INTERVAL,BIKE_INS_VEHICLE_TYPE_ID,COMMERCIAL_BIKE_INS_VEHICLE_TYPE_ID, STATUS_CODES, OCR_EXIST_PARENT_ID, VEHICLE_USAGE_COMMERCIAL } from '@src/config/constant';
import { autoFillData, callServiceToCreateLead, getAddonList, isShowDynamicField } from '@common/FunctionList';
import InsuranceService from '@src/services/InsuranceService';
import AddApplicationThankPopup from './AddApplicationThankPopup';
import moment from 'moment';
import { isMobile, isBrowser } from "react-device-detect";


export default function CreateApplication(props: any) {
    const commonDataRef = useRef<any>({})
    const [page, setPage] = useState<number>(1)
    const [loading, setLoading] = useState(false)
    const [documents, setDocuments] = useState<any>({})
    const [documentErrors, setDocumentErrors] = useState<any>({})
    const [sectionList, setSectionList] = useState<any>([])
    const [refresh, setRefresh] = useState(false)
    // eslint-disable-next-line
    const [masterData, setMasterData] = useState<any>({})
    const [orcrApiCall, setOrcrApiCall] = useState<boolean>(false)
    const [documentList, setDocumentList] = useState<Array<any>>([])
    const [showThankyouPopup, setShowThankyouPopup] = useState(false)
    const [leadId, setLeadId] = useState(null)
    const [vehicleDetailsAutoFill, setVehicleDetailsAutoFill] = useState(false)
    // eslint-disable-next-line
    const [searchParams, setSearchParams] = useSearchParams()

    const subRoute: any = searchParams.get('step');
    const isEditMode = searchParams.get('edit') === 'true';
    const dispatch: any = useDispatch()

    const insuranceState = useSelector((state: any) => state.insurance)
    const commonState = useSelector((state: any) => state.common)

    const navigate: any = useNavigate()
    const queryParams = useParams();
    const leadHashId = queryParams.id
    // Existing document-resubmission flow uses path id without edit flag
    const resubmissionLeadId = (!isEditMode && leadHashId) ? leadHashId : undefined
    const editLeadId = (isEditMode && leadHashId) ? leadHashId : undefined

    const[updateMakeYear, SetUpdateMakeYear] = useState(false);

    let { user_data } = secureStorage.getItem("authAgentUserInfo") || {};

    const getCreateStepUrl = (step: string) => {
        const path = leadHashId ? `/create-insurance-policy/${leadHashId}` : '/create-insurance-policy'
        const params = new URLSearchParams({ step })
        if (isEditMode) params.set('edit', 'true')
        return `${path}?${params.toString()}`
    }

    const getVehicleFieldValue = (filledSectionList: any, fieldName: string) => {
        const vehicleSection = filledSectionList?.find((item: any) => item.key === 'vehicle_details')
        const fields = vehicleSection?.blocks?.flatMap((block: any) => block?.fields || []) || []
        const field = fields.find((f: any) => f.name === fieldName)
        if (field?.value === undefined || field?.value === null || field?.value === '') return undefined
        if (typeof field.value === 'object') return field.value?.value
        return field.value
    }

    /** Vehicle fields required before Premium Plan can load partners / quotes */
    const hasRequiredVehicleData = (leadData: any, filledSectionList?: any) => {
        const vehicleType = getVehicleFieldValue(filledSectionList, 'vehicle_classification')
            ?? leadData?.vehicle_type ?? leadData?.vehicle_type_id
            ?? insuranceState?.createInsurance?.vehicle_type
        const vehicleUsage = filledSectionList
            ?.find((item: any) => item.key === 'vehicle_details')
            ?.blocks?.[0]?.fields?.find((f: any) => f.name === 'vehicle_classification')?.value?.vehicle_usage
            ?? leadData?.vehicle_usage
            ?? insuranceState?.createInsurance?.vehicle_usage
        const insuranceType = getVehicleFieldValue(filledSectionList, 'insurance_type')
            ?? leadData?.insurance_type ?? leadData?.insurance_type_id
            ?? insuranceState?.createInsurance?.insurance_type
        const classificationId = getVehicleFieldValue(filledSectionList, 'classification_id')
            ?? leadData?.classification_id
            ?? insuranceState?.createInsurance?.classification_id

        if (!(vehicleType || vehicleUsage)) return false
        if (classificationId === undefined || classificationId === null || classificationId === '') return false
        if (insuranceType === undefined || insuranceType === null || insuranceType === '') return false

        if (+vehicleUsage === VEHICLE_USAGE_COMMERCIAL) {
            const commercial = getVehicleFieldValue(filledSectionList, 'commercial_type')
                ?? leadData?.commercial_type ?? leadData?.commercial_type_id
                ?? insuranceState?.createInsurance?.commercial_type
            if (commercial === undefined || commercial === null || commercial === '') return false
        }
        return true
    }

    const ensureRuleEngineForEdit = (leadData: any, filledSectionList: any) => {
        const createData = insuranceState?.createInsurance || {}
        // Always retain MMV from lead when edit state was loaded asynchronously
        const mmvFromLead = {
            lead_id: createData.lead_id ?? leadData?.lead_id ?? leadData?.leadId,
            make: createData.make ?? leadData?.make,
            make_name: createData.make_name ?? leadData?.make_name,
            model: createData.model ?? leadData?.model,
            model_name: createData.model_name ?? leadData?.model_name,
            version: createData.version ?? leadData?.version,
            version_name: createData.version_name ?? leadData?.version_name,
            parent_model_id: createData.parent_model_id ?? leadData?.parent_model_id,
            parent_model_name: createData.parent_model_name ?? leadData?.parent_model_name,
        }
        const existingRuleEngine = createData.rule_engine ?? leadData?.rule_engine
        const insuranceType = createData.insurance_type
            ?? leadData?.insurance_type
            ?? leadData?.insurance_type_id
            ?? getVehicleFieldValue(filledSectionList, 'insurance_type')

        if (existingRuleEngine !== undefined && existingRuleEngine !== null && existingRuleEngine !== '') {
            if (createData.rule_engine == null || createData.make == null || createData.make_name == null || createData.lead_id == null) {
                dispatch(saveInsurance({
                    ...createData,
                    ...mmvFromLead,
                    rule_engine: existingRuleEngine,
                    insurance_type: insuranceType,
                    application_type: APP_TYPE_AGENCY
                }))
            }
            return
        }

        const vehicleType = getVehicleFieldValue(filledSectionList, 'vehicle_classification')
            ?? leadData?.vehicle_type ?? leadData?.vehicle_type_id
        const mfgYear = getVehicleFieldValue(filledSectionList, 'make_year') ?? leadData?.make_year
        const diff = [BIKE_INS_VEHICLE_TYPE_ID, COMMERCIAL_BIKE_INS_VEHICLE_TYPE_ID].includes(+vehicleType)
            ? +commonState?.common?.motorcycle_rule_engine_max_year
            : +commonState?.common?.rule_engine_max_year
        const upperRange: any = commonState?.common?.make_year?.[0]?.value
        const lowerRange = upperRange - diff
        const ruleEngine = (lowerRange <= +mfgYear && +mfgYear <= upperRange) ? WITH_RULE_ENGINE : WITHOUT_RULE_ENGINE

        dispatch(saveInsurance({
            ...createData,
            ...mmvFromLead,
            rule_engine: ruleEngine,
            insurance_type: insuranceType,
            application_type: APP_TYPE_AGENCY
        }))
    }

    const fetchFormConfig = async (masterData: any) => {
        const result = await dispatch(getFormConfig())
        if (result?.length) {
            updateSectionList(result, masterData)
        }
    }

    const updateSectionList = (sectionList: any, masterData: any) => {
        if (sectionList?.length) {
            let updatedList = JSON.parse(JSON.stringify(sectionList))
            updatedList.forEach((sectionItem: any) => {
                sectionItem?.blocks?.forEach((blockItem: any) => {
                    blockItem?.fields?.forEach((fieldItem: any) => {
                        if (fieldItem.name in masterData) {
                            // for make_year when motocycle is selected the populate bike_make_year as options
                            if (fieldItem.name === "make_year" && updateMakeYear) {
                                fieldItem['options'] = masterData['bike_make_year'];
                            } else if (fieldItem.name in masterData) {
                                // use make_year as options
                                fieldItem['options'] = masterData[fieldItem.name];
                            }
                        }
                    });
                });
            });
            setSectionList([...updatedList])
            dispatch(updateFormConfig(updatedList || []));
        } else {
            navigate('/create-insurance-policy?step=document_upload')
            toast.error("Error in fetching form config")
        }
    }

    const fetchDocumentList = () => {
        dispatch(getDocumentListing({
            application_type_id: 4
        })).then((res: any) => {
            if (editLeadId) {
                loadEditLeadDetails(res)
            } else if (resubmissionLeadId) {
                getResubmissionDocs(res)
            } else {
                setDocumentList(res)
                setLoading(false)
            }
        }).catch(() => {
            setLoading(false)
        })
    }

    const fetchMasterConfig = async () => {
        setLoading(true);
        if (!commonState?.common || !Object.keys(commonState?.common)?.length) {
            dispatch(getMasterConfig()).then((resp: any) => {
                if (resp.status === STATUS_CODES['success']) {
                    setMasterData(resp.data);
                    fetchFormConfig(resp.data);
                }
            });
        }
        else {
            setMasterData(commonState.common);
            fetchFormConfig(commonState.common);
        }

    }

    const checkIfORCRUploaded = async () => {
        let docs = commonDataRef.current.getData()
        if (docs?.[DOCUMENT_CATEGORY['orcr']]?.length && !orcrApiCall) {
            //call api
            await getTextFromDoc(docs[DOCUMENT_CATEGORY['orcr']])
        } else if (page < 3 && !docs?.[DOCUMENT_CATEGORY['orcr']]?.length) {
            dispatch(updateInsuranceDataForAgency({}))
        }
    }

    const getTextFromDoc = async (data: any) => {
        let formdata = new FormData()
        formdata.append('user_id', '99')

        for (let file of data) {
            formdata.append('files', file)
        }

        setLoading(true)
        //api call
        await dispatch(uploadAndReadDocument(formdata))
        setOrcrApiCall(true)
        setLoading(false)
        dispatch(saveInsurance({ ...insuranceState?.createInsurance }))
    }

    /**
     * Edit Application: allow direct jump to Premium Plan when prior data exists;
     * otherwise send the user to the step that still needs data.
     */
    const handleNavLinkRouting = (stepIndex: number, stepKey: string, event?: any) => {
        if (!isEditMode) return

        if (stepKey === 'premium_plan') {
            const leadData = insuranceState?.resubmissionLeadData || {}
            if (!hasRequiredVehicleData(leadData, sectionList)) {
                event?.preventDefault()
                toast.error("Please complete Vehicle Details before accessing Premium Plan")
                setPage(Math.max(page, 2))
                navigate(getCreateStepUrl('vehicle_details'))
                return
            }
            ensureRuleEngineForEdit(leadData, sectionList)
            if (page < 3) setPage(3)
        } else if (stepKey === 'vehicle_details') {
            if (page < 2) setPage(2)
        }
    }

    const validateDocuments = () => {
        let docs = commonDataRef.current.getData()
        setDocuments(docs)

        let list = documentList
        let errorOb: any = {}
        let isError = true;

        const hasValidExistingSrc = (srcList: any) =>
            !!srcList?.filter((e: any) => e?.src && !docs?.deletedItems?.some((d: any) => Number(d?.id ?? d) === Number(e.id)))?.length

        // In edit mode, pre-filled OCR/docs on the item or its children count as uploaded
        const hasExistingDoc = (item: any) => {
            if (!isEditMode) return false
            if (hasValidExistingSrc(item?.src)) return true

            if (item?.child?.length) {
                const selectedChild = item.child.find((c: any) => c.value === item.value)
                if (hasValidExistingSrc(selectedChild?.src)) return true

                // ORCR / Existing Policy: images are stored on children ("upload any one")
                if (item.type === 'single_selection' || item.value === OCR_EXIST_PARENT_ID) {
                    return item.child.some((c: any) => hasValidExistingSrc(c?.src))
                }
            }
            return false
        }

        const hasUploadedDoc = (item: any) => {
            if (docs?.[item.value]) return true
            if (item?.child?.length) {
                const selectedChild = item.child.find((c: any) => c.value === item.value)
                if (selectedChild && docs?.[selectedChild.value]) return true
                if (item.type === 'single_selection' || item.value === OCR_EXIST_PARENT_ID) {
                    return item.child.some((c: any) => docs?.[c.value])
                }
            }
            return false
        }

        for (let ob of list) {
            // ORCR / Existing Policy group — require any one (new upload or existing image)
            if (ob.type === 'single_selection' || ob.value === OCR_EXIST_PARENT_ID
                || [DOCUMENT_CATEGORY['orcr'], DOCUMENT_CATEGORY['existing_policy']].includes(ob.value)) {
                const isRequired = ob.is_required || ob.child?.some((c: any) => c.is_required)
                if (isRequired && !hasUploadedDoc(ob) && !hasExistingDoc(ob)) {
                    isError = false;
                    errorOb[ob.value] = `${ob.label} Field is required`;
                }
                continue
            }

            if (ob.child.length) {
                for (let childdoc of ob.child) {
                    if (childdoc.is_required && !docs?.[childdoc.value] && !hasExistingDoc(childdoc)) {
                        isError = false;
                        errorOb[childdoc.value] = `${childdoc.label} Field is required`;
                    }
                }
            }
            else if (ob.is_required && !docs?.[ob.value] && !hasExistingDoc(ob)) {
                isError = false;
                errorOb[ob.value] = `${ob.label} Field is required`;
            }
        }

        // Replace errors so stale messages are not shown for valid existing docs
        setDocumentErrors(errorOb);
        return isError;
    }

    const updateAllSectionList = (type: string) => {
        let fields = commonDataRef.current.getData()
        let newSectionList: any = JSON.parse(JSON.stringify(sectionList))
        switch (type) {
            case 'vehicle_details': {
                fields = [...fields?.vehicle_details, ...fields?.customer_details]
                const resolveFieldVal = (field: any) => {
                    if (field?.value === undefined || field?.value === null || field?.value === '') return undefined
                    if (typeof field.value === 'object') return field.value?.value
                    return field.value
                }
                let insurance_type = resolveFieldVal(fields?.find((e: any) => e.name === 'insurance_type'))
                    ?? insuranceState?.createInsurance?.insurance_type
                    ?? insuranceState?.resubmissionLeadData?.insurance_type
                    ?? insuranceState?.resubmissionLeadData?.insurance_type_id
                let vehicle_classification_type = resolveFieldVal(fields?.find((e: any) => e.name === 'vehicle_classification'))
                //calculate rule engine using mfg-year
                let mfgYear = fields?.find((e: any) => e.name === 'make_year')
                let diff =  [BIKE_INS_VEHICLE_TYPE_ID,COMMERCIAL_BIKE_INS_VEHICLE_TYPE_ID].includes(+vehicle_classification_type) ? +commonState?.common?.motorcycle_rule_engine_max_year : +commonState?.common?.rule_engine_max_year
                let upperRange: any = commonState?.common?.make_year?.[0]?.value
                let lowerRange = upperRange - diff
                let ruleEngine = (lowerRange <= mfgYear?.value?.value && mfgYear?.value?.value <= upperRange) ? WITH_RULE_ENGINE : WITHOUT_RULE_ENGINE
                const vehicleDetails = newSectionList.find((item: any) => item.key === 'vehicle_details')

                let updatedList = vehicleDetails?.blocks?.map((item: any) => {
                    let newItem: any = { ...item }
                    let allFields = newItem?.fields

                    let updatedFields = allFields?.map((ele: any) => {
                        let updatedField = { ...ele }
                        let searchedItem = fields?.find((e: any) => e.name === ele.name)

                        if (searchedItem) {
                            updatedField = { ...searchedItem }
                            // Keep date values serializable for Redux
                            if (updatedField?.value instanceof Date) {
                                updatedField.value = moment(updatedField.value).format('YYYY-MM-DD')
                            }
                        }
                        return updatedField
                    })
                    newItem['fields'] = updatedFields
                    return newItem
                })

                newSectionList[1].blocks = updatedList
                //for reseting addons
                // if (prevInsuranceType && prevInsuranceType !== insurance_type) {
                //     updatedFields['addon_detail'] = null
                // }

                dispatch(saveInsurance({ ...insuranceState?.createInsurance, rule_engine: ruleEngine, application_type: APP_TYPE_AGENCY, insurance_type }))
                //mark auto fill true
                // dispatch(updateInsuranceDataForAgency({ ...insuranceState?.insuranceData, autoFillVehicleDetails: true }))
                break
            }

            default: {
                break
            }
        }
        setSectionList(newSectionList)
        dispatch(updateFormConfig(newSectionList))
        return newSectionList
    }

    const isValidAddon = async (addonList: any): Promise<{ checkValue: boolean, message: Array<any> }> => {
        let flag = true;
        let message: any = []
        let { rule_engine, partner_slug } = insuranceState?.createInsurance

        if (addonList.length > 0) {
            for (let data of addonList) {
                if (data.id === 1) {
                    if (!data["coverage_amount"]) {
                        message.push("Please select coverge amount for Bodily Injured");
                        flag = false
                    }
                    if (!data["premium_amount"]) {
                        message.push("Please Enter Premium amount for Bodily Injured");
                        flag = false
                    }
                } else if (data.id === 2) {
                    if (!data["coverage_amount"]) {
                        message.push("Please select coverge amount for Property Damage");
                        flag = false
                    }
                    if (!data["premium_amount"]) {
                        message.push("Please Enter Premium amount for Property Damage");
                        flag = false
                    }
                } else if (data.id === 3) {
                    if (!data["coverage_amount"]) {
                        message.push("Please select coverge amount for Personal Accident");
                        flag = false
                    }
                    // if (rule_engine === 2 && !data["premium_amount"]) {
                    //     message.push("Please Enter Premium amount for Personal Accident");
                    //     flag = false
                    // }
                    if ((data["premium_amount"] === "" || data["premium_amount"] === undefined) && ['oona', 'milestone'].includes(partner_slug)) {
                        let premiumAmtValidation = true;
                        if (data['milestone_coverage_amount_limit'] || data['oona_coverage_amount_limit']) {
                            if (data['milestone_coverage_amount_limit'] === data['coverage_amount'] || data['oona_coverage_amount_limit'] === data['coverage_amount']) {
                                premiumAmtValidation = false;
                            }
                        }

                        if (premiumAmtValidation || rule_engine === WITHOUT_RULE_ENGINE) {

                            message.push("Please Enter Premium amount for Personal Accident");
                            flag = false
                        }
                    }

                    if (!data["seat_capacity"]) {
                        message.push("Please select seat capacity for Personal Accident");
                        flag = false
                    }
                }
            }

        }
        return new Promise((resolve) => resolve({ checkValue: flag, message }))
    }

    const validateFormDetails = () => {
        let errorFlag = true
        let fields = commonDataRef.current.getData()
        fields = subRoute === 'vehicle_details' ? [...fields?.vehicle_details, ...fields?.customer_details] : fields

        if (!fields?.length) {
            errorFlag = false
        }
        fields.forEach((ele: any) => {
            if (ele.name === 'vehicle') {
                let requiredKeys = ['make', 'model', 'version']

                ele['error_message'] = ''

                for (let key of requiredKeys) {
                    if (!((insuranceState?.createInsurance?.[key] && insuranceState?.createInsurance?.[key] !== 0) || insuranceState?.createInsurance?.[key + "_name"])) {
                        errorFlag = false
                        ele['error_message'] = ele.error_msg
                        setRefresh(!refresh)
                        break
                    }
                }

                if (!ele.value) {
                    errorFlag = false
                    ele['error_message'] = ele.error_msg
                    setRefresh(!refresh)
                }
            }
            else {

                const value = ele.value
                const fieldVisible = isShowDynamicField(ele, [{ fields }])
               
                if (fieldVisible && ele.is_required && !value) {
                    if (ele.name === 'engine_capacity' && !updateMakeYear) {

                    }else{
                        ele['error_message'] = ele.error_msg
                        errorFlag = false
                        setRefresh(!refresh)
                    }
                }

                if (['plate_number', 'mv_number'].includes(ele.name) && value) {
                    let alphanumericPattern = /^[a-z0-9]+$/i;
                    if (!alphanumericPattern.test(value)) {
                        ele['error_message'] = "Plate number should be alphanumeric"
                        errorFlag = false
                        setRefresh(!refresh)
                    }
                }

                if (ele.name === "effectivity_date" || ele.name === "expiry_date") {
                    let effectivityDate = fields.find((data: any) => data?.name === "effectivity_date")?.value;
                    let expiryDate = fields.find((data: any) => data?.name === "expiry_date")?.value;
            
                    if (!expiryDate || !effectivityDate) {
                        let missingField = !expiryDate ? 'expiry_date' : 'effectivity_date';
                        if (ele.name === missingField) {
                            ele['error_message'] = `Insurance policy ${missingField.replaceAll('_', " ")} is required`;
                            errorFlag = false;
                            setRefresh(!refresh);
                        }
                    } else {
                        let checkIfExpiryValid = moment(expiryDate).isBefore(effectivityDate);
                        let checkIfEffDateValid = moment(expiryDate).startOf('day').diff(moment(effectivityDate).startOf('day'), 'days') < 60;
                        let errorMessage = checkIfEffDateValid
                            ? `Interval between Expiry date and Effectivity date should be of ${EFFECTIVITY_EXPIRY_INTERVAL} days`
                            : null;
            
                        if (checkIfExpiryValid && ele.name === "expiry_date") {
                            ele['error_message'] = `The Expiry date cannot be earlier than the Effectivity date.`;
                            errorFlag = false;
                            setRefresh(!refresh);
                        } else if (checkIfEffDateValid && ele.name === "expiry_date") {
                            ele['error_message'] = errorMessage;
                            errorFlag = false;
                            setRefresh(!refresh);
                        }
                    }
                }
    
                if (ele.name === 'car_fmv' && ele.value) {           
                    const vehicleType = fields.find((data: any) => data?.name === "vehicle_classification")?.value?.value   
                    if ([BIKE_INS_VEHICLE_TYPE_ID, COMMERCIAL_BIKE_INS_VEHICLE_TYPE_ID].includes(vehicleType)) {
                        if (+ele.value > +ele?.validation?.bike_max_length) {
                            ele['error_message'] = `Fmv of vehicle cannot exceed ${ele?.validation?.bike_max_length}`
                            errorFlag = false;
                            setRefresh(!refresh);
                        } else if (+ele.value < +ele?.validation?.bike_min_length) { // apply minimum condition
                            ele['error_message'] = `Fmv of vehicle cannot less than ${ele?.validation?.bike_min_length}`
                            errorFlag = false
                            setRefresh(!refresh);
                        } 
                    }else if (ele.name === "car_fmv" && (value.toString().length < 6 || value.toString().length > 9)) {
                        ele['error_message'] = 'FMV of car must be between 6 to 9 digits';
                        errorFlag = false
                        setRefresh(!refresh)
                    }
                }
            }
        })

        return errorFlag
    }

    const validatePlan = (data: any) => {
        const quoteData = data
        const insurance_type = insuranceState?.createInsurance?.insurance_type
        //if validation flag is set to false then give error
        if (insurance_type !== INS_TYPE_CTPL && (!quoteData.base_issue_rate || +quoteData.base_issue_rate === 0)) {
            toast.error('Please enter valid base issue rate')
            return false
        }
        else if (!quoteData?.validation) {
            toast.error('Please select valid plan')
            return false
        }
        return true
    }

    const onsavenext = async () => {
        if (resubmissionLeadId) {
            setLoading(true);
            try {
                //code for resubmit api call
                let docs = commonDataRef.current.getData()
                let { lead_id } = insuranceState?.createInsurance
                setLeadId(lead_id);
                const docUrls: any = [];
                const comments = docs.comments;
                for (const key in docs) {
                    if (!isNaN(Number(key))) {
                        const filesOrObjects = docs[key];
                        if (Array.isArray(filesOrObjects)) {
                            for (const file of filesOrObjects) {
                                if (file instanceof File) {
                                    const formData = new FormData();
                                    formData.append('lead_id', lead_id);
                                    formData.append('files', file);

                                    // Generate URL
                                    const response: any = await generateDocUrls(formData);
                                    const uploadedFile = response[0];
                                    // Add to docUrls
                                    const fileData: any = {
                                        url: uploadedFile.file_url,
                                        document_id: Number(key),
                                    };
                                    if (comments?.[key]) {
                                        fileData["comment"] = comments[key];
                                    }
                                    docUrls.push(fileData);
                                }
                            }
                        } else if (typeof filesOrObjects === "object") {
                            // Process single objects
                            for (const subKey in filesOrObjects) {
                                const file = filesOrObjects[subKey];
                                if (file instanceof File) {
                                    const formData = new FormData();
                                    formData.append('lead_id', lead_id);
                                    formData.append('files', file);

                                    // Generate URL
                                    const response: any = await generateDocUrls(formData);
                                    const uploadedFile = response[0];

                                    // Add to docUrls
                                    const fileData: any = {
                                        url: uploadedFile.file_url,
                                        document_id: Number(key),
                                    };
                                    if (comments?.[key]) {
                                        fileData["comment"] = comments[key];
                                    }
                                    docUrls.push(fileData);
                                }
                            }
                        }
                    }
                }

                if (!docUrls?.length) return toast.error("Please select atleast 1 document to proceed")

                const requestPayload = {
                    lead_id: lead_id,
                    doc_path: docUrls,
                    is_resubmit: true,
                    created_by: user_data?.id,
                    created_by_name: user_data?.name
                };
                const addDoc: any = await addDocumentWeb(requestPayload);
                //soft delete images
                if (docs?.deletedItems?.length && addDoc.status === STATUS_CODES['success']) {
                    const deletedIds = docs.deletedItems.map((d: any) => d?.id ?? d)
                    await updateDocumentStatus({ lead_id, documents: deletedIds })
                }
                setLoading(false);
                setShowThankyouPopup(true);
                return
            } catch (error) {
                console.error("Error during resubmission:", error);
            } finally {
                setLoading(false); // Stop loading when all operations are done
            }
        }

        //code for normal flow
        switch (subRoute) {
            case 'upload_document': {
                const validate = validateDocuments()
                if (!validate) break

                if (!resubmissionLeadId) await checkIfORCRUploaded()
                if (page < 2) setPage(2)
                navigate(getCreateStepUrl('vehicle_details'))
                break
            }
            case 'vehicle_details': {
                const validate = validateFormDetails()
                if (!validate) break

                if (page < 3) setPage(3)
                updateAllSectionList('vehicle_details')

                dispatch(updateInsuranceDataForAgency({ ...insuranceState?.insuranceData, autoFillVehicleDetails: true }))
                navigate(getCreateStepUrl('premium_plan'))

                break
            }
            case 'premium_plan': {
                const { addonList, quotes, partnerDetails } = commonDataRef.current.getData()
    
                let { insurance_type } = insuranceState?.createInsurance || {}
                if (!addonList?.length && !(insurance_type === INS_TYPE_CTPL && updateMakeYear)) {
                    toast.error("Please select addon to proceed")
                    break
                }

                const addons = getAddonList({ original_list: addonList }, null, insuranceState?.createInsurance?.rule_engine)
                const { checkValue, message } = await isValidAddon(addons)
                if (!checkValue) {
                    for (let msg of message) {
                        toast.error(msg);
                    }
                    break
                }

                //validate selected plan
                const noError = validatePlan(quotes)
                if (!noError) break

                submitLead(sectionList, { addon_detail: addons, ...partnerDetails, ...quotes })
                // navigate('/create-insurance-policy?step=premium_plan')
                break
            }
        }
    }

    /** Map saved lead / create state into createAgencyLead field names for Edit Application */
    const getEditLeadPayloadFallbacks = (leadData: any = {}, createData: any = {}) => {
        const src = { ...leadData, ...createData }
        const carFmv = src.car_fmv ?? src.fmv_car ?? src.total_fmv ?? src.total_fmv_car
        const address = src.address ?? src.per_full_address ?? src.cur_full_address
        const formatDate = (value: any) => {
            if (!value) return undefined
            if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10)
            const parsed = moment(value)
            return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined
        }

        return {
            insurance_type: src.insurance_type ?? src.insurance_type_id,
            rule_engine: src.rule_engine ?? src.rule_engine_id,
            car_under_finance: src.car_under_finance ?? src.is_under_financing,
            financier: src.financier ?? src.financier_id,
            financier_name: src.financier_name,
            car_fmv: carFmv,
            acc_description: src.acc_description ?? src.accessories_description,
            vehicle_type: src.vehicle_type ?? src.vehicle_type_id ?? src.vehicle_classification,
            vehicle_usage: src.vehicle_usage ?? src.vehicle_id,
            classification_id: src.classification_id,
            commercial_type: src.commercial_type ?? src.commercial_type_id,
            make: src.make,
            model: src.model,
            version: src.version,
            make_name: src.make_name,
            model_name: src.model_name,
            version_name: src.version_name,
            parent_model_id: src.parent_model_id,
            parent_model_name: src.parent_model_name,
            make_year: src.make_year ?? src.mfg_year,
            plate_number: src.plate_number ?? src.plate_no,
            mv_number: src.mv_number,
            color: src.color,
            chassis_number: src.chassis_number,
            machine_number: src.machine_number,
            fuel_type: typeof src.fuel_type === 'object' ? src.fuel_type?.value : src.fuel_type,
            transmission: src.transmission,
            seat_capacity: src.seat_capacity ?? src.vehicle_seat,
            engine_capacity: src.engine_capacity,
            name: src.name ?? src.customer_name,
            phone: src.phone ?? src.mobile,
            alt_phone: src.alt_phone ?? src.alternate_mobile,
            email: src.email,
            dob: formatDate(src.dob),
            address,
            notes: src.notes ?? src.lead_notes,
            tenure: src.tenure,
            effectivity_date: formatDate(src.effectivity_date),
            expiry_date: formatDate(src.expiry_date),
            received_date: formatDate(src.received_date),
            partner_id: Array.isArray(src.partner_id) ? src.partner_id[0] : src.partner_id,
            partner_slug: Array.isArray(src.partner_slug) ? src.partner_slug[0] : src.partner_slug,
        }
    }

    const fillMissingPayloadFields = (target: any, fallbacks: any) => {
        Object.keys(fallbacks || {}).forEach((key) => {
            const current = target[key]
            const fallback = fallbacks[key]
            if ((current === undefined || current === null || current === '')
                && fallback !== undefined && fallback !== null && fallback !== '') {
                target[key] = fallback
            }
        })
        return target
    }

    /** Edit createAgencyLead rejects these keys when lead_id is present */
    const EDIT_DISALLOWED_PAYLOAD_KEYS = [
        'agency_id',
        'user_id',
        'total_fmv',
        'acc_price',
        'per_full_address',
        'cur_full_address',
        'per_sameas_cur',
        'phone',
        'email',
        'dob',
        'alt_phone',
        'transmission'
    ]

    const stripEditDisallowedPayloadKeys = (payload: any) => {
        EDIT_DISALLOWED_PAYLOAD_KEYS.forEach((key) => {
            if (payload && Object.prototype.hasOwnProperty.call(payload, key)) {
                delete payload[key]
            }
        })
        return payload
    }

    const submitLead = async (sections: any, additionalParams?: any) => {
        setLoading(true)
        const createData = insuranceState?.createInsurance || {}
        const leadData = insuranceState?.resubmissionLeadData || {}
        // Edit Application may skip Vehicle Details — keep MMV from saved lead data
        const make = createData.make ?? leadData.make
        const make_name = createData.make_name ?? leadData.make_name
        const model = createData.model ?? leadData.model
        const model_name = createData.model_name ?? leadData.model_name
        const version = createData.version ?? leadData.version
        const version_name = createData.version_name ?? leadData.version_name
        const parent_model_id = createData.parent_model_id ?? leadData.parent_model_id
        const parent_model_name = createData.parent_model_name ?? leadData.parent_model_name
        const rule_engine = createData.rule_engine ?? leadData.rule_engine
        const insurance_type = createData.insurance_type ?? leadData.insurance_type ?? leadData.insurance_type_id
        const partner_slug = createData.partner_slug ?? leadData.partner_slug
        const partner_id = createData.partner_id ?? leadData.partner_id
            
        let { addon_detail, base_issue_rate, net_premium, discount,referrer_mark_up } = additionalParams || {}
       
        if (!addon_detail?.length && !(insurance_type === INS_TYPE_CTPL && updateMakeYear)) {
            setLoading(false)
            return toast.error("Please select addons")
        }
        
        const params = callServiceToCreateLead(JSON.parse(JSON.stringify(sections)))
        const reqParam: any = {
            ...params,
            make, make_name,
            model, model_name,
            version, version_name,
            lead_type: 6,
            apptype: APP_TYPE_AGENCY,
            rule_engine,
            partner_id: Array.isArray(partner_id) ? partner_id[0] : partner_id,
            partner_slug: Array.isArray(partner_slug) ? partner_slug[0] : partner_slug,
            add_on_rate: base_issue_rate,
            addon_detail:addon_detail ?? [],
            tenure: params.tenure || 1,
            source: 1,
            parent_model_id:parent_model_id,
            parent_model_name:parent_model_name
        }

        //append net_premium when net_premium is edited on plans page
        if (rule_engine === WITHOUT_RULE_ENGINE) {
            reqParam['net_premium'] = +net_premium
        }
        if(discount) {
            reqParam['discount'] = +discount
        }
        if(referrer_mark_up) {
            reqParam['referrer_mark_up'] = +referrer_mark_up
        }

        if (isEditMode) {
            // Jumping to Premium Plan skips Vehicle Save — fill any missing mandatory fields from lead
            fillMissingPayloadFields(reqParam, getEditLeadPayloadFallbacks(leadData, createData))

            const resolvedLeadId = createData.lead_id
                ?? leadData.lead_id
                ?? leadData.leadId
                ?? leadId
            if (resolvedLeadId) {
                reqParam['lead_id'] = resolvedLeadId
            }

            if (!reqParam.tenure) {
                reqParam.tenure = leadData.tenure || 1
            }

            // Edit schema rejects these keys
            stripEditDisallowedPayloadKeys(reqParam)
        }

        let result = await InsuranceService.createAgencyLead(reqParam)

        if (result?.data?.data?.hashId) {
            let { hashId, leadId } = result.data.data
            setLeadId(leadId)
            dispatch(updateInsuranceDataForAgency({}))
            const hasNewDocuments = Object.keys(documents || {}).some((key) => {
                if (isNaN(Number(key))) return false
                const files = documents[key]
                if (!files) return false
                return Array.from(files).some((file: any) => file instanceof File)
            })
            const hasDeletedDocuments = isEditMode && !!documents?.deletedItems?.length
            if (isEditMode && !hasNewDocuments && !hasDeletedDocuments) {
                setShowThankyouPopup(true)
                setLoading(false)
                dispatch(saveInsurance({}))
            } else {
                submitDocument({ hashId })
            }
        }
        else {
            toast.error(result.data.message)
            setLoading(false)
        }
    }

    const submitDocument = async (params: any) => {
        let { hashId } = params

        if (Object.keys(documents).length) {
            let docArray = []
            let formdata = new FormData()
            let leadId = hashId

            formdata.append('lead_id', leadId)
            formdata.append('user_id', user_data?.id)
            formdata.append('isNewCreated', "1")
            formdata.append('apptype', "" + APP_TYPE_AGENCY)

            for (let key in documents) {
                // Skip non-document keys (e.g. deletedItems, comments)
                if (isNaN(Number(key))) continue

                let docType = 1
                let files = documents[key]
                if (!files) continue

                for (let file of Array.from(files)) {
                    if (!(file instanceof File)) continue

                    let docObj = {
                        document_id: Number(key),
                        type: 'add',
                        document_type_id: +docType
                    }
                    docArray.push(docObj)
                    formdata.append('files', file)
                }
            }

            // Edit Application: include soft-deleted existing docs in addDocumentWeb payload
            if (isEditMode && documents?.deletedItems?.length) {
                for (const deleted of documents.deletedItems) {
                    const docId = Number(deleted?.id ?? deleted)
                    const documentId = Number(deleted?.document_id)
                    if (!docId || !documentId) continue

                    docArray.push({
                        document_id: documentId,
                        type: 'delete',
                        document_type_id: Number(deleted?.document_type_id || 1),
                        id: docId
                    })
                }
            }

            if (!docArray.length) {
                setShowThankyouPopup(true)
                setLoading(false)
                dispatch(saveInsurance({}))
                return
            }

            formdata.append('doc_path', JSON.stringify(docArray))
            //api call
            let uploadDocument: any = await addDocument(formdata)
            //if upload successfull
            if (uploadDocument && uploadDocument.status === STATUS_CODES['success']) {
                setShowThankyouPopup(true)
                setLoading(false)
                dispatch(updateInsuranceDataForAgency({}))
                dispatch(saveInsurance({}))
            } else {
                setLoading(false)
            }
        }
    }

    const getResubmissionDocs = async (docList: any) => {
        const leadData = await dispatch(getLeadDetails({ apptype: APP_TYPE_AGENCY, leadId: resubmissionLeadId }))
        const resubmitDocs = leadData?.documents?.filter((e: any) => e?.is_resubmit)

        const keysToUpdate = ['documents', 'partner_id', 'rule_engine',
            'application_type_id', 'insurance_type', 'make', 'make_name', 'model', 'model_name',
            'version', 'version_name', 'lead_id']

        let updatedInsuranceState: any = {}
        for (let key of keysToUpdate) {
            if (leadData?.[key]) {
                if (key === 'application_type_id') {
                    updatedInsuranceState['application_type'] = leadData[key]
                } else updatedInsuranceState[key] = leadData[key]
            }
        }

        if (!resubmitDocs?.length) {
            navigate('/create-insurance-policy')
            setDocumentList(docList)
            setLoading(false)
            dispatch(saveInsurance({}))
            return
        }

        dispatch(saveInsurance({ ...insuranceState?.createInsurance, ...updatedInsuranceState }))

        let updatedDocumentList = JSON.parse(JSON.stringify(docList))

        for (let doc of updatedDocumentList) {
            let findDOc = resubmitDocs?.find((el: any) => el.id === doc?.value)
            if (findDOc) {
                doc['src'] = findDOc?.path?.map((e: any) => ({
                    src: e.src,
                    id: e.id,
                    document_id: Number(doc.value),
                    document_type_id: 1
                }))
                doc['is_resubmit'] = true
                doc['comment'] = findDOc?.comment
            }

            if (doc?.child?.length) {
                let child = doc.child
                for (let subDoc of child) {
                    let findSubDoc = resubmitDocs?.find((el: any) => el.id === subDoc?.value)

                    if (findSubDoc) {
                        subDoc['src'] = findSubDoc?.path?.map((e: any) => ({
                            src: e.src,
                            id: e.id,
                            document_id: Number(subDoc.value),
                            document_type_id: 1
                        }))
                        subDoc['is_resubmit'] = true
                        subDoc['comment'] = findSubDoc?.comment
                    }
                }
            }
        }

        setDocumentList(updatedDocumentList)
        setLoading(false)
        setPage(4)
    }

    const loadEditLeadDetails = async (docList: any) => {
        const leadData = await dispatch(getLeadDetails({ apptype: APP_TYPE_AGENCY, leadId: editLeadId }))

        if (!leadData || !leadData.leadId) {
            toast.error("Unable to load application details")
            navigate('/insurance-list')
            setLoading(false)
            return
        }

        const keysToUpdate = ['documents', 'partner_id', 'rule_engine',
            'application_type_id', 'insurance_type', 'make', 'make_name', 'model', 'model_name',
            'version', 'version_name', 'partner_slug', 'parent_model_id', 'parent_model_name',
            'classification_id', 'commercial_type', 'commercial_type_id', 'vehicle_type', 'vehicle_usage']

        let updatedInsuranceState: any = {
            lead_id: leadData.leadId || leadData.lead_id
        }
        for (let key of keysToUpdate) {
            if (leadData?.[key] !== undefined && leadData?.[key] !== null) {
                if (key === 'application_type_id') {
                    updatedInsuranceState['application_type'] = leadData[key]
                } else if (key === 'commercial_type_id' && updatedInsuranceState.commercial_type == null) {
                    updatedInsuranceState['commercial_type'] = leadData[key]
                } else if (key !== 'commercial_type_id') {
                    updatedInsuranceState[key] = leadData[key]
                }
            }
        }

        setLeadId(leadData.leadId || leadData.lead_id)
        // Prefer lead payload over any in-memory create state so MMV is never dropped
        dispatch(saveInsurance({ ...updatedInsuranceState }))

        let fetchedDocs = insuranceState?.editDocuments || []
        // Fallback if edit docs were cleared (e.g. page refresh)
        if (!fetchedDocs.length && editLeadId) {
            const docsResponse: any = await dispatch(fetchAllDocuments({ lead_id: editLeadId }))
            if (docsResponse?.status !== STATUS_CODES['success']) {
                toast.error(docsResponse?.message || "Failed to fetch documents. Please try again.")
                navigate('/insurance-list')
                setLoading(false)
                return
            }
            fetchedDocs = docsResponse?.data || []
        }

        const updatedDocumentList = mapFetchedDocumentsToDocList(docList, fetchedDocs)
        setDocumentList(updatedDocumentList)
        setLoading(false)
        // Unlock all sidebar steps so Premium Plan is clickable in edit mode
        setPage(3)
        setSearchParams({ step: 'upload_document', edit: 'true' })
    }

    const mapFetchedDocumentsToDocList = (docList: any[], fetchedDocs: any[] = []) => {
        const updatedDocumentList = JSON.parse(JSON.stringify(docList || []))
        const docsById: any = {}

        for (const doc of fetchedDocs) {
            const docId = doc?.doc_id
            if (!docId) continue
            if (!docsById[docId]) docsById[docId] = []
            docsById[docId].push({
                id: doc.id,
                document_id: Number(docId),
                document_type_id: Number(doc.doc_type_id || 1),
                src: doc.path || doc.short_path || doc.unsigned_url || ''
            })
        }

        const applyDocs = (item: any) => {
            if (docsById[item.value]?.length) {
                item.src = docsById[item.value]
                item.is_edit_doc = true
            }
            if (item?.child?.length) {
                item.child.forEach((child: any) => applyDocs(child))

                // ORCR / Existing Policy: keep each child's docs independent; surface one section on parent for display
                if (item.type === 'single_selection' || item.value === OCR_EXIST_PARENT_ID) {
                    const childWithDocs = item.child.find((c: any) => c.value === DOCUMENT_CATEGORY['orcr'] && c?.src?.length)
                        || item.child.find((c: any) => c?.src?.length)
                    if (childWithDocs?.src?.length) {
                        item.src = childWithDocs.src
                        item.is_edit_doc = true
                        if (item.value === OCR_EXIST_PARENT_ID || item.type === 'single_selection') {
                            item.value = childWithDocs.value
                            item.label = childWithDocs.label
                        }
                    }
                }
            }
        }

        updatedDocumentList.forEach((item: any) => applyDocs(item))
        return updatedDocumentList
    }

    /**
     * Autofill vehicle details for resubmission / edit so Premium Plan can prefill from lead data
     */
    const autoFillVehicleDetails = async (currentSectionList: any) => {
        const leadData = insuranceState?.resubmissionLeadData || {}
        const createInsurance = insuranceState?.createInsurance || {}
        const mergedData = {
            ...createInsurance,
            ...leadData,
            make_name: leadData?.make_name || createInsurance?.make_name,
            model_name: leadData?.model_name || createInsurance?.model_name,
            version_name: leadData?.version_name || createInsurance?.version_name,
            make: leadData?.make ?? createInsurance?.make,
            model: leadData?.model ?? createInsurance?.model,
            version: leadData?.version ?? createInsurance?.version,
            classification_id: leadData?.classification_id ?? createInsurance?.classification_id,
            commercial_type: leadData?.commercial_type ?? leadData?.commercial_type_id ?? createInsurance?.commercial_type,
            vehicle_type: leadData?.vehicle_type ?? createInsurance?.vehicle_type,
            vehicle_usage: leadData?.vehicle_usage ?? createInsurance?.vehicle_usage,
        }
        const updatedData = await autoFillData(mergedData, currentSectionList?.[1])

        const updatedSectionList = JSON.parse(JSON.stringify(currentSectionList))
        updatedSectionList[1] = updatedData
        setSectionList(updatedSectionList)
        dispatch(updateFormConfig(updatedSectionList))
        setVehicleDetailsAutoFill(true)
        return updatedSectionList
    }

    useEffect(() => {
        setLoading(true)
        fetchMasterConfig()
        fetchDocumentList()

        return (() => {
            dispatch(saveInsurance({}))
            dispatch(setEditDocuments([]))
            dispatch(setResubmissionLeadData({}))
        })
        //eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (!subRoute) {
            if (isEditMode) {
                setSearchParams({ step: 'upload_document', edit: 'true' })
            } else {
                setSearchParams({ step: 'upload_document' })
            }
        }
        //eslint-disable-next-line
    }, [subRoute])

    /**
     * for handling auto fill of vehicle details in resubmission/edit case 
     */
    useEffect(() => {
        const resubData = insuranceState?.resubmissionLeadData
        if ((resubmissionLeadId || editLeadId) && resubData && Object.keys(resubData)?.length && sectionList?.length && !vehicleDetailsAutoFill) {
            autoFillVehicleDetails(sectionList).then((filledList: any) => {
                if (editLeadId) {
                    ensureRuleEngineForEdit(resubData, filledList)
                }
            })
        }

        //eslint-disable-next-line
    }, [resubmissionLeadId, editLeadId, insuranceState?.resubmissionLeadData, sectionList])

    // Edit Application: keep loader while Premium Plan waits for vehicle autofill
    useEffect(() => {
        if (isEditMode && editLeadId && subRoute === 'premium_plan' && !vehicleDetailsAutoFill) {
            setLoading(true)
        }
        //eslint-disable-next-line
    }, [isEditMode, editLeadId, subRoute, vehicleDetailsAutoFill])

    
    return (
        <>
            <FullPageLoader show={loading} />
            <div className="container-fluid add-insurnce-outer">
                <div className="top-heading financer-lead-heding">
                    <h1>{isEditMode ? 'Edit Application' : 'Create Application'}</h1> 
                </div>
                <div className="lead-detail-tabs">
                    {
                        sectionList && <div className="detail-form-sec">
                            <div className="menu-tab-left-panel">
                                <div className="dealer-tab-left-panel nav">
                                    <ul>
                                        {
                                            sectionList.map((sectionItem: any, index: any) => {
                                                const leadPathId = leadHashId || ''
                                                const stepQuery = isEditMode
                                                    ? `?step=${sectionItem['key']}&edit=true`
                                                    : `?step=${sectionItem['key']}`
                                                // Edit Application: all steps are clickable so user can jump to Premium Plan
                                                const isStepClickable = isEditMode || (page > index)
                                                return (
                                                    <li key={index}>
                                                        {
                                                            isStepClickable ?
                                                                <NavLink to={`/create-insurance-policy${leadPathId ? ('/' + leadPathId) : ''}${stepQuery}`} onClick={(e) => { handleNavLinkRouting(index + 1, sectionItem['key'], e) }} className={(index + 1) < page ? "completed" : ""} >
                                                                    <span className="img-type">
                                                                        {isMobile && (index+1) }
                                                                    </span>
                                                                    {sectionItem['title']}</NavLink>
                                                                :
                                                                <a href="#!" onClick={(e) => e.preventDefault()}>
                                                                    <span className="img-type">
                                                                        {isMobile && (index+1) }
                                                                    </span>
                                                                    {sectionItem['title']}
                                                                </a>

                                                        }
                                                    </li>
                                                )
                                            })
                                        }
                                    </ul>
                                </div>
                            </div>

                            <div className="route-files">
                                <div className=" insurance-create-outer relative " style={{ "display": "block" }}>
                                    <div className="loan-expect-left-panle">
                                        <div className="loan-form">
                                            <div className={`loan-form-filed-scroll ${(searchParams?.get('step') === 'upload_document') ? `quote-scroll` : ''}`}>
                                                {
                                                    subRoute === "upload_document"
                                                    && <div className="loan-filed-sec">
                                                        <div className="row">
                                                            <div className="col-md-12 w-100">
                                                                <h2>Upload Documents</h2>
                                                            </div>
                                                            <DocumentForm ref={commonDataRef} docList={documentList} setDocuments={setDocuments} errors={documentErrors} documents={documents} updateDocList={(params: any) => setDocumentList(params)} setDocumentErrors={setDocumentErrors} setOrcrApiCall={setOrcrApiCall} resubmissionLeadId={resubmissionLeadId} isEditMode={isEditMode} />
                                                        </div>
                                                    </div>
                                                }
                                                {
                                                    subRoute === 'vehicle_details'
                                                    && <VehicleDetails ref={commonDataRef} setLoading={setLoading} documents={documents} setPage={setPage} resubmissionLeadId={resubmissionLeadId} isEditMode={isEditMode} setSectionList={setSectionList} SetUpdateMakeYear={SetUpdateMakeYear}/>
                                                }
                                                {
                                                    subRoute === 'premium_plan'
                                                    && (!editLeadId || vehicleDetailsAutoFill)
                                                    && <Plans ref={commonDataRef} setLoading={setLoading} sectionList={sectionList} sectionItem={sectionList?.[2]} setPage={setPage} resubmissionLeadId={resubmissionLeadId} isEditMode={isEditMode} />
                                                }
                                                {

                                                    <div className="btn-continue btn-bottom-fixed">

                                                        {
                                                            ((resubmissionLeadId && subRoute === 'upload_document')
                                                                || !resubmissionLeadId)
                                                            &&
                                                            <button className="btn-primary " onClick={() => { onsavenext() }}>{
                                                                resubmissionLeadId ? 'Resubmit Docs' : 'Save & Continue'
                                                            }</button>
                                                        }

                                                    </div>

                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>
            {
                showThankyouPopup && <AddApplicationThankPopup show={showThankyouPopup} handleClose={() => {
                    setShowThankyouPopup(false)
                    navigate(`/insurance-list`)
                }} leadId={leadId} />
            }
        </>
    )
}